import React, { useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Container,
  DialogContentText,
  Stack,
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers';
import { Dayjs } from '../../../utils/dayjs-config';
import { convertTo12Hour, formatTimeRange } from '../../../time/timeUtils';
import { BusinessConfig, Employee, isBlocker } from '../../../types';
import { useAdminData } from '../../../contexts/AdminDataContext';
import LoadingSpinner from '../../../components/global/LoadingSpinner';
import AppointmentSection from '../../../components/employee/components/MyAppointments/AppointmentSection';
import SpecificDayScheduleCard from '../../../components/employee/components/MySchedule/SpecificDateScheduleCard';
import ConfirmationDialog from '../../../components/global/ConfirmationDialog';
import SuccessOrErrorDialog from '../../../components/global/SuccessOrErrorDialog';
import { useScheduleHubContext } from '../ScheduleHubViewModel';
import { useEmployeeScheduleManager } from './ManageScheduleViewModel';
import EmployeePicker from '../../../components/admin/EmployeePicker';
import { useQuery } from '@tanstack/react-query';
import { getCurrentBusinessConfig } from '../../../firebase/firebase-delegate';
import { resolveBookingUxConfig } from '../../../utils/bookingUxConfig';

export type EmployeeSchedulePanelProps = {
  employee: Employee;
  selectedDate: Dayjs;
  showAppointments?: boolean;
  alwaysOpenScheduleEditor?: boolean;
};

export const EmployeeSchedulePanel: React.FC<EmployeeSchedulePanelProps> = ({
  employee,
  selectedDate,
  showAppointments = true,
  alwaysOpenScheduleEditor = false,
}) => {
  const { currentEmployeeQuery } = useAdminData();

  const {
    employeeDisplayName,
    isLoading,
    isSaving,
    isSetUpError,
    setUpErrorMessage,
    workHoursTemplate,
    workHours,
    selectedDateAppointments,
    employeeAppointmentsMap,
    handleScheduleChange,
    confirmationOpen,
    confirmScheduleChange,
    handleCancelConfirmation,
    affectedAppointments,
    resultDialogOpen,
    onCloseResultDialog,
    mutationSuccess,
  } = useEmployeeScheduleManager({
    employee,
    selectedDate,
  });
  const isEditingOwnSchedule = currentEmployeeQuery.data?.id === employee.id;
  const scheduleTargetLabel = isEditingOwnSchedule ? 'your schedule' : `${employeeDisplayName}'s schedule`;
  const isScheduleEditorBusy = isLoading || isSaving;

  return (
    <Box>
      {isSetUpError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load schedule data: {setUpErrorMessage}
        </Alert>
      )}

      <SuccessOrErrorDialog
        open={resultDialogOpen}
        onClose={onCloseResultDialog}
        success={mutationSuccess}
        successTitle="Schedule Updated!"
        successMessage={`${employeeDisplayName}'s changes were saved successfully.`}
        errorTitle="Error"
        errorMessage="Something went wrong, please refresh the page or try again later."
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {workHoursTemplate && (
          <SpecificDayScheduleCard
            date={selectedDate}
            initialWorkHours={workHours}
            isLoading={isScheduleEditorBusy}
            onWorkHoursChange={handleScheduleChange}
            workHours={workHoursTemplate}
            lockedOpen={alwaysOpenScheduleEditor}
          />
        )}
      </Box>

      {isLoading && <LoadingSpinner />}

      {showAppointments && (
        <AppointmentSection
          appointments={selectedDateAppointments}
          employeeAppointmentsMap={employeeAppointmentsMap}
          date={selectedDate}
          isLoading={isLoading}
        />
      )}

      <ConfirmationDialog
        open={confirmationOpen}
        onClose={handleCancelConfirmation}
        title="Confirm Schedule Change"
        message={`Are you sure you want to change ${scheduleTargetLabel} on ${selectedDate.format('dddd, MMMM DD')}?`}
        isLoading={isSaving}
        loadingLabel="Saving..."
        content={
          affectedAppointments.length > 0 && (
            <>
              <DialogContentText sx={{ mt: 2 }}>
                The following appointments or unavailable blocks will be removed:
              </DialogContentText>
              <ul>
                {affectedAppointments.map((app) => {
                  if (isBlocker(app)) {
                    const blockerLabel = app.notes || app.appointmentTitle || 'Unavailable';
                    return (
                      <li key={app.id}>
                        {blockerLabel} block from {formatTimeRange(app.appointmentTime, app.appointmentDuration)}
                      </li>
                    );
                  }
                  const serviceName = app.appointmentTitle;
                  const customerName = app.customerFirstName || app.customerLastName || 'a customer';
                  return (
                    <li key={app.id}>
                      {serviceName} with {customerName} at {convertTo12Hour(app.appointmentTime)}
                    </li>
                  );
                })}
              </ul>
              <DialogContentText sx={{ mt: 2 }}>
                You can go back and change each appointment accordingly if you do not want to cancel it.
              </DialogContentText>
            </>
          )
        }
        onConfirm={confirmScheduleChange}
        confirmButtonLabel="Confirm"
        cancelButtonLabel="Cancel"
      />
    </Box>
  );
};

const ManageSchedule: React.FC = () => {
  const hubContext = useScheduleHubContext();
  const { currentEmployeeQuery, employeesQuery } = useAdminData();
  const businessConfigQuery = useQuery<BusinessConfig | null>({
    queryKey: ['businessConfig'],
    queryFn: () => getCurrentBusinessConfig(),
  });
  const bookingUxConfig = resolveBookingUxConfig(businessConfigQuery.data);
  const isResourceMode = bookingUxConfig.bookingModel === 'resource';
  const resourceSingular = bookingUxConfig.terminology.resourceSingular;
  const resourcePlural = bookingUxConfig.terminology.resourcePlural;
  const ownerId = businessConfigQuery.data?.paymentInfo?.owner?.trim() || '';
  const currentEmployee = currentEmployeeQuery.data ?? null;
  const employees = employeesQuery.data ?? [];

  const permittedEmployees = useMemo(() => {
    const baseEmployees = (isResourceMode && ownerId)
      ? employees.filter((employee) => employee.id !== ownerId)
      : employees;
    const sortedBaseEmployees = [...baseEmployees].sort((left, right) =>
      `${left.firstName} ${left.lastName}`.localeCompare(`${right.firstName} ${right.lastName}`)
    );
    if (!currentEmployee) return [];
    if (currentEmployee.admin || (isResourceMode && currentEmployee.id === ownerId)) {
      return sortedBaseEmployees;
    }
    return sortedBaseEmployees.filter((employee) => employee.id === currentEmployee.id);
  }, [currentEmployee, employees, isResourceMode, ownerId]);

  useEffect(() => {
    if (permittedEmployees.length === 0) {
      return;
    }
    const employeeStillExists = permittedEmployees.some((employee) => employee.id === hubContext.selectedEmployeeId);
    if (!hubContext.selectedEmployeeId || !employeeStillExists) {
      const defaultEmployeeId = permittedEmployees[0].id;
      hubContext.onSelectedEmployeeIdChange(defaultEmployeeId);
    }
  }, [hubContext, permittedEmployees]);

  const selectedEmployee = useMemo(
    () => permittedEmployees.find((employee) => employee.id === hubContext.selectedEmployeeId) ?? null,
    [hubContext.selectedEmployeeId, permittedEmployees]
  );

  if (currentEmployeeQuery.isLoading || employeesQuery.isLoading) {
    return <LoadingSpinner />;
  }

  if (currentEmployeeQuery.isError || employeesQuery.isError || !currentEmployee) {
    return (
      <Alert severity="error">
        {`Failed to load ${resourcePlural.toLowerCase()}. Please refresh and try again.`}
      </Alert>
    );
  }

  return (
    <Container disableGutters>
      {currentEmployee.admin && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
            sx={{ mb: 1, width: 'fit-content' }}
          >
            <EmployeePicker
              employees={permittedEmployees}
              value={hubContext.selectedEmployeeId}
              onChange={hubContext.onSelectedEmployeeIdChange}
              label={resourceSingular}
              placeholder={`Select ${resourceSingular.toLowerCase()}`}
              allOptionLabel={`All ${resourcePlural}`}
            />
          </Stack>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <DateCalendar
          value={hubContext.sharedDate}
          onChange={(next) => {
            if (next) {
              hubContext.onSharedDateChange(next);
            }
          }}
          disablePast
        />
      </Box>

      {!selectedEmployee ? (
        <Alert severity="info">{`No ${resourcePlural.toLowerCase()} found.`}</Alert>
      ) : (
        <EmployeeSchedulePanel
          employee={selectedEmployee}
          selectedDate={hubContext.sharedDate}
        />
      )}
    </Container>
  );
};

export default ManageSchedule;
